import React, { useState } from "react";
import { motion } from "framer-motion";
import { Globe, ChevronDown } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

interface LanguageSelectorProps {
  isDark: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ isDark }) => {
  const { currentLanguage, setLanguage, availableLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = availableLanguages.find(
    (lang) => lang.code === currentLanguage
  );

  const handleLanguageChange = (langCode: string) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-8 px-2 rounded-lg border border-border/60 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors"
        title="Select Language"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="hidden sm:inline font-medium uppercase">
          {currentLang?.code || "EN"}
        </span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {/* Language Dropdown */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`absolute right-0 top-full mt-2 w-48 rounded-xl border shadow-lg ${
            isDark
              ? "border-neutral-700 bg-neutral-900/95"
              : "border-gray-200 bg-white/95"
          }`}
        >
          <div className="p-2">
            <div
              className={`px-3 py-2 text-sm font-medium ${
                isDark ? "text-neutral-300" : "text-slate-600"
              }`}
            >
              Select Language
            </div>
            <div className="border-t border-neutral-700 dark:border-neutral-700 my-1"></div>
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                  currentLanguage === lang.code
                    ? isDark
                      ? "bg-sky-950/40 text-sky-300"
                      : "bg-sky-50 text-sky-800"
                    : isDark
                    ? "text-neutral-300 hover:bg-neutral-800"
                    : "text-slate-600 hover:bg-gray-100"
                }`}
              >
                <span>{lang.nativeName}</span>
                <span className="text-xs opacity-70">{lang.name}</span>
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default LanguageSelector;
